import { useMemo, useState, useEffect } from 'react'

import { Text } from '@wartek-id/text-v1'
import type {
  CustomListProps,
  OptionItem as CustomSelectOptionItem,
} from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'

import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'

type IndikatorPerencanaanMultiSelectProps = {
  contents: {
    label?: string
    orderNumber?: number
    title: string
    upper?: React.ReactNode
    beforeOptions?: React.ReactNode
    afterOptions?: React.ReactNode
  }
  allowDuplicate?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  options: Array<OptionItem>
  selected?: Array<OptionItem>
  onChange: (selected: Array<OptionItem>) => void
}

// New option type to have a better ergonomics
export type OptionItem = {
  label: string
  value: string
}

export function IndikatorPerencanaanMultiSelect({
  allowDuplicate = false,
  contents,
  isLoading,
  isDisabled,
  options,
  selected = [],
  onChange,
}: IndikatorPerencanaanMultiSelectProps) {
  // ------------------------------------------------------------
  // Empty selection
  // ------------------------------------------------------------
  const [showEmptySelection, setShowEmptySelection] = useState(
    selected.length === 0
  )

  const handleAddEmptySelection = () => {
    setShowEmptySelection(true)

    // TODO: set the empty selection input to be focused
  }
  const handleRemoveEmptySelection = () => {
    if (selected.length > 0) {
      setShowEmptySelection(false)
    } else {
      setShowEmptySelection(true)
    }
  }

  // Hide empty selection if there is at least one selected item
  useEffect(
    handleRemoveEmptySelection,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  )

  // ------------------------------------------------------------
  // Form handlers
  // ------------------------------------------------------------
  const handleRemoveItem = (value: string, index: number) => {
    let newSelected = []

    if (allowDuplicate) {
      // If allow duplicate, remove the item based on the index
      newSelected = [...selected]
      newSelected.splice(index, 1)
      onChange(newSelected)
    } else {
      // If not allow duplicate, remove the item based on the value
      newSelected = selected.filter((selectedItem) => {
        return selectedItem.value !== String(value)
      })
    }

    // Send update via onChange
    onChange(newSelected)
  }

  const handleAddItem = (item: CustomSelectOptionItem) => {
    const newSelected = [...selected, toOptionItem(item)]
    onChange(newSelected)

    setShowEmptySelection(false)
  }

  const handleChangeItem = (item: CustomSelectOptionItem, index: number) => {
    const newSelected = [...selected]
    newSelected[index] = toOptionItem(item)

    onChange(newSelected)
  }

  // ------------------------------------------------------------
  // Resolve options
  // ------------------------------------------------------------

  const resolvedOptions: Array<CustomSelectOptionItem> = useMemo(() => {
    // If allow duplicate, return the options as it is
    if (allowDuplicate) {
      // Cast the options to the CustomSelectOptionItem type
      return options.map(toCustomSelectOptionItem)
    }

    // If not allow duplicate but there is no selected items, return the options as it is
    if (selected.length === 0) {
      // Cast the options to the CustomSelectOptionItem type
      return options.map(toCustomSelectOptionItem)
    }

    return (
      options
        // If not allow duplicate, filter out the selected options
        .filter(
          (option) =>
            !selected.some((selectedItem) => {
              return selectedItem.value == option.value
            })
        )
        // Cast the options to the CustomSelectOptionItem type
        .map(toCustomSelectOptionItem)
    )
  }, [options, selected, allowDuplicate])

  // Helper function to get options for a specific selected item
  // This ensures the current item is included in options so it can be displayed
  const getOptionsForItem = (
    currentItem: OptionItem
  ): Array<CustomSelectOptionItem> => {
    if (allowDuplicate) {
      return resolvedOptions
    }

    // Include the current item in options so it can be displayed
    const currentItemOption = toCustomSelectOptionItem(currentItem)
    return [currentItemOption, ...resolvedOptions]
  }

  return (
    <div className="space-y-5">
      <IndikatorPerencanaanPanel
        variant="active"
        label={contents.label}
        orderNumber={contents.orderNumber}
        title={contents.title}
        upperContent={contents.upper}
      >
        {contents.beforeOptions}

        <div className="space-y-3">
          {selected.map((item, index) => {
            return (
              <div className="flex w-full gap-4" key={item.value + index}>
                <div className="w-full">
                  <CustomSelect
                    isDisabled={isLoading || isDisabled}
                    options={getOptionsForItem(item)}
                    placeholder="Pilih di sini"
                    value={toCustomSelectOptionItem(item)}
                    customDisplayValue={DisplayValue}
                    customList={ListItem}
                    onChange={(item: CustomSelectOptionItem) =>
                      handleChangeItem(item, index)
                    }
                  />
                </div>
                <Button
                  color="white"
                  disabled={isLoading || isDisabled}
                  onClick={() => handleRemoveItem(item.value, index)}
                >
                  Hapus
                </Button>
              </div>
            )
          })}

          {showEmptySelection && resolvedOptions.length > 0 && !isLoading && (
            <div className="flex w-full gap-4">
              <div className="w-full">
                <CustomSelect
                  isDisabled={isLoading || isDisabled}
                  options={resolvedOptions}
                  placeholder="Pilih di sini"
                  customDisplayValue={DisplayValue}
                  customList={ListItem}
                  onChange={handleAddItem}
                />
              </div>
              <Button
                color="white"
                disabled={isLoading || selected.length === 0 || isDisabled}
                onClick={handleRemoveEmptySelection}
              >
                Hapus
              </Button>
            </div>
          )}
        </div>

        {contents.afterOptions}
      </IndikatorPerencanaanPanel>

      {!isDisabled && (
        <Button
          color="white"
          size="sm"
          className="items-center gap-1"
          disabled={
            showEmptySelection || isLoading || resolvedOptions.length === 0
          }
          onClick={handleAddEmptySelection}
        >
          <Icon color="disabled">add_circle</Icon>
          Tambah
        </Button>
      )}
    </div>
  )
}

function DisplayValue({ item }: { item: CustomSelectOptionItem }) {
  return (
    <Text variant="body" className="text-justify" as="span">
      {item.label}
    </Text>
  )
}

function ListItem({ item, selected, active }: CustomListProps) {
  return (
    <div className="flex items-center gap-2 pr-4">
      <Text variant="body" color={active ? 'inverse' : 'default'} as="span">
        {(item as CustomSelectOptionItem).label}
      </Text>
      {selected && (
        <Icon
          fontSize="sm"
          color={active ? 'inverse' : 'informational'}
          className="absolute right-3"
        >
          check
        </Icon>
      )}
    </div>
  )
}

function toCustomSelectOptionItem(option: OptionItem): CustomSelectOptionItem {
  return {
    name: option.value,
    label: option.label,
  }
}

function toOptionItem(option: CustomSelectOptionItem): OptionItem {
  return {
    label: option.label,
    value: option.name,
  }
}
